import { useCallback, useEffect, useState } from 'react';
import {
  useIsLocked,
  useIsHomingInProgress,
  useEStop,
  useSendCommand,
  useHomed,
  useJointPositions,
} from '@/store/store';
import { useMachineStore } from '@/store/store';
import { ConnectionState, HOMING_JOINTS } from '@/types/machine.types';
import styles from '@/components/SafetyOverlay/SafetyOverlay.module.css';

// Machine-absolute (G53) readout, 3 decimals — matches the DRO formatting.
function formatAxisPos(value: number | undefined): string {
  const v = typeof value === 'number' ? value : 0;
  return (Math.abs(v) < 0.001 ? 0 : v).toFixed(3);
}

// After this long stuck in STARTING, escalate the message so a stuck LinuxCNC
// boot is visible instead of an eternally cheerful "starting…". We keep
// retrying (and never fall back to SIM) — this only changes the wording.
const STARTING_STALL_MS = 15_000;

export function SafetyOverlay(): JSX.Element | null {
  const isLocked = useIsLocked();
  const isHoming = useIsHomingInProgress();
  const homed = useHomed();
  const jointPositions = useJointPositions();
  const estop = useEStop();
  const sendCommand = useSendCommand();
  const setHomingInProgress = useMachineStore((s) => s.setHomingInProgress);
  const connectionState = useMachineStore((s) => s.connectionState);
  const machineMode = useMachineStore((s) => s.machineMode);
  const isConnected = connectionState === ConnectionState.CONNECTED;
  const isStarting = isConnected && machineMode === 'STARTING';

  // Timer that flips on once we've been STARTING too long. Reset whenever we
  // leave the STARTING state so a later restart starts the clock fresh.
  const [startingStalled, setStartingStalled] = useState(false);
  useEffect(() => {
    if (!isStarting) return;
    const t = setTimeout(() => setStartingStalled(true), STARTING_STALL_MS);
    // Reset on cleanup (i.e. when we leave STARTING) so a later restart begins
    // the stall clock fresh instead of showing "not responding" immediately.
    return () => {
      clearTimeout(t);
      setStartingStalled(false);
    };
  }, [isStarting]);

  const handleStartHome = useCallback(() => {
    setHomingInProgress(true);
    sendCommand({ type: 'HOME_ALL' });
  }, [sendCommand, setHomingInProgress]);

  const handleResetEstop = useCallback(() => {
    sendCommand({ type: 'ESTOP_TOGGLE' });
  }, [sendCommand]);

  // Connection takes priority — nothing can be controlled while the socket is down.
  if (!isConnected) {
    return (
      <div className={styles.safetyOverlay} role="alert" aria-live="assertive">
        <div className={styles.safetyOverlayContent}>
          <h1 className={styles.safetyOverlayTitle}>5-AXIS CONTROL SYSTEM</h1>
          <div className={styles.safetyOverlayLoader}>
            <div className={styles.safetyOverlaySpinner} />
            <p className={styles.safetyOverlayStatus}>Connection Lost</p>
            <p className={styles.safetyOverlaySubstatus}>Reconnecting to machine…</p>
          </div>
        </div>
      </div>
    );
  }

  // LinuxCNC is present but not connected yet — the app launched it and is
  // waiting for the controller. Show a loading overlay (never SIM on a real
  // machine); after a stall, escalate the wording but keep retrying.
  if (isStarting) {
    return (
      <div className={styles.safetyOverlay} role="alert" aria-live="assertive">
        <div className={styles.safetyOverlayContent}>
          <h1 className={styles.safetyOverlayTitle}>5-AXIS CONTROL SYSTEM</h1>
          <div className={styles.safetyOverlayLoader}>
            <div className={styles.safetyOverlaySpinner} />
            <p className={styles.safetyOverlayStatus}>
              {startingStalled ? 'LinuxCNC not responding' : 'Starting machine…'}
            </p>
            <p className={styles.safetyOverlaySubstatus}>
              {startingStalled
                ? 'Still retrying — check the controller.'
                : 'Bringing up the LinuxCNC controller…'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!isLocked) return null;

  return (
    <div className={styles.safetyOverlay} role="alert" aria-live="assertive">
      <div className={styles.safetyOverlayContent}>
        <h1 className={styles.safetyOverlayTitle}>5-AXIS CONTROL SYSTEM</h1>

        {estop ? (
          <div className={styles.safetyOverlayEstopWarning}>
            <div className={styles.safetyOverlayEstopIcon}>⚠️</div>
            <p className={styles.safetyOverlayEstopText}>E-STOP ACTIVE — Machine is locked</p>
            <button className={styles.btnEstopReset} onClick={handleResetEstop} type="button">
              RESET E-STOP
            </button>
          </div>
        ) : isHoming ? (
          <div className={styles.safetyOverlayLoader}>
            <div className={styles.safetyOverlaySpinner} />
            <p className={styles.safetyOverlayStatus}>Homing in Progress...</p>
            <p className={styles.safetyOverlaySubstatus}>
              Please wait until all axes are referenced.
            </p>
            <ul className={styles.homeAxisList}>
              {HOMING_JOINTS.map((joint) => {
                // Two states for now: homed (done) vs not homed (waiting).
                // A dedicated "failed" state is deferred (no per-axis signal).
                const isDone = homed[joint.homedIndex] === 1;
                return (
                  <li
                    key={joint.label}
                    className={`${styles.homeAxisRow} ${isDone ? styles.homeAxisDone : styles.homeAxisWaiting}`}
                  >
                    <span className={styles.homeAxisCap}>{joint.label}</span>
                    <span className={styles.homeAxisValue}>
                      {formatAxisPos(jointPositions[joint.homedIndex])}
                    </span>
                    <span className={styles.homeAxisStatus}>
                      {isDone ? (
                        <>
                          <svg
                            className={styles.homeAxisIcon}
                            viewBox="0 0 24 24"
                            aria-hidden="true"
                          >
                            <path
                              d="M5 13l4 4L19 7"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                          <span className={styles.homeAxisLabel}>HOMED</span>
                        </>
                      ) : (
                        <>
                          <span className={styles.homeAxisRing} aria-hidden="true" />
                          <span className={styles.homeAxisLabel}>WAITING</span>
                        </>
                      )}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : (
          <button className={styles.btnStartHome} onClick={handleStartHome} type="button">
            START & HOME ALL
          </button>
        )}
      </div>
    </div>
  );
}
